import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faSpinner, faRotateRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { previewEmail } from '../../../../../service/notification/notification-service';
import './EmailPreview.css';

interface EmailPreviewProps {
    /** Notification category string sent to the backend (e.g. "APPOINTMENT_CONFIRMED"). */
    category: string;
    /** Subject / title line passed to the template renderer. */
    title: string;
    /** Custom body text the business owner typed into the textarea. */
    body: string;
    /**
     * Business ID passed to the preview endpoint so the backend can apply
     * the correct brand (name, color, logo) and substitute sample variables.
     * Pass null to render with the default template.
     */
    businessId: number | null;
}

/**
 * Two-state email preview widget.
 *
 * CLOSED → single "Build email" button (eye icon).  Clicking fetches the
 *          branded HTML from notification-service and opens the iframe panel.
 *
 * OPEN   → "Refresh preview" button (rotate icon) that re-fetches so edits
 *          in the textarea are reflected immediately, keeping the panel open;
 *          plus a small "Close" link that collapses the panel without
 *          re-fetching.
 *
 * The iframe is sandboxed (no scripts, no same-origin) so email-specific CSS
 * cannot leak into the app.
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({ category, title, body, businessId }) => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();

    const [html, setHtml] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    const fetchPreview = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const rendered = await previewEmail(token, businessId, category, title, body);
            setHtml(rendered);
            setOpen(true);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Erro desconhecido';
            setError(msg);
            setOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    // The iframe is sandboxed, so it can't read the app's --gs-* theme vars.
    // Track the current theme and bake a concrete backdrop color into the
    // preview HTML instead. Re-renders when the header toggles data-theme.
    const [isDark, setIsDark] = useState(
        () =>
            typeof document !== 'undefined' &&
            document.documentElement.getAttribute('data-theme') === 'dark'
    );
    useEffect(() => {
        const el = document.documentElement;
        const obs = new MutationObserver(() =>
            setIsDark(el.getAttribute('data-theme') === 'dark')
        );
        obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
        return () => obs.disconnect();
    }, []);

    /**
     * Re-tint ONLY the email's outer "mat" (the area around the white card) so
     * the preview blends with the app theme. The white message card and the
     * actual sent email are untouched — this style is injected only into the
     * sandboxed preview iframe.
     */
    const withPreviewBackdrop = (raw: string): string => {
        const mat = isDark ? '#14131a' : '#f4f4f7';
        const style =
            `<style>html,body{background-color:${mat} !important;}` +
            `body>table[role="presentation"]{background-color:${mat} !important;}</style>`;
        return raw.includes('</head>')
            ? raw.replace('</head>', `${style}</head>`)
            : style + raw;
    };

    return (
        <div className="email-preview">
            <div className="email-preview__actions">
                {!open ? (
                    /* CLOSED state — primary "Build email" button */
                    <button
                        type="button"
                        className="email-preview__btn"
                        onClick={fetchPreview}
                        disabled={loading}
                        aria-expanded={false}
                    >
                        {loading ? (
                            <FontAwesomeIcon icon={faSpinner} spin className="email-preview__btn-icon" />
                        ) : (
                            <FontAwesomeIcon icon={faEye} className="email-preview__btn-icon" />
                        )}
                        {loading ? t('settings.emailPreview.loading') : t('settings.emailPreview.open')}
                    </button>
                ) : (
                    /* OPEN state — refresh + close */
                    <>
                        <button
                            type="button"
                            className="email-preview__btn"
                            onClick={fetchPreview}
                            disabled={loading}
                            aria-expanded={true}
                        >
                            {loading ? (
                                <FontAwesomeIcon icon={faSpinner} spin className="email-preview__btn-icon" />
                            ) : (
                                <FontAwesomeIcon icon={faRotateRight} className="email-preview__btn-icon" />
                            )}
                            {loading ? t('settings.emailPreview.loading') : t('settings.emailPreview.refresh')}
                        </button>
                        <button
                            type="button"
                            className="email-preview__close-btn"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faXmark} className="email-preview__btn-icon" />
                            {t('settings.emailPreview.close')}
                        </button>
                    </>
                )}
            </div>

            {open && (
                <div className="email-preview__panel">
                    {error ? (
                        <div className="email-preview__error" role="alert">
                            <strong>{t('settings.emailPreview.errorPrefix')}</strong> {error}
                        </div>
                    ) : html !== null ? (
                        <iframe
                            className="email-preview__iframe"
                            srcDoc={withPreviewBackdrop(html)}
                            title={t('settings.emailPreview.refresh')}
                            sandbox=""
                        />
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default EmailPreview;
